import { Box, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../../api/config/getPublicConfig";
import { qualifyNewLead } from "../../../api/user/qualifyNewLead";
import { qualifyNewLeadForContact } from "../../../api/user/qualifyNewLeadForContact";
import APAccordion from "../../../components/APAccordion";
import APButton from "../../../components/buttons/APButton";
import { APButtonLink } from "../../../components/buttons/APButtonLink";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import HomeButton from "../../../components/buttons/HomeButton";
import DossierSavingsChart from "../../../components/charts/APCharts/dossierSavings/DossierSavingsChart";
import DossierSelector from "../../../components/dossier/DossierSelector";
import EmptyList from "../../../components/EmptyList";
import FaqList from "../../../components/faq/FaqList";
import MenuCard from "../../../components/MenuCard";
import PageContainer from "../../../components/PageContainer";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import { getCRMClientURL } from "../../../helpers/configuration";
import {
  getOpportunityCRMUrl,
  isDossierActive,
} from "../../../helpers/dossiers";
import { openInNewTab } from "../../../helpers/routes";
import { useDossierActivationCheck } from "../../../hooks/useDossierActivationCheck";
import { useFaqs } from "../../../hooks/useFaqs";
import { useAuth } from "../../../providers/AuthProvider";
import { useConfirm } from "../../../providers/ConfirmProvider";
import { DossierStatusLabelMap, Opportunity } from "../../../types/Dossier";

const DossierActivated = () => {
  const navigate = useNavigate();
  const { getScopedPageUrl, inAppScope } = useAuth();
  const { dossierId } = useParams<{
    dossierId: string;
  }>();
  const confirm = useConfirm();

  const {
    navigationCheckComplete,
    isLoading: isLoadingDossierActivationCheck,
    isSuccess: isSuccessDossierActivationCheck,
    activatedDossiers,
    lastestOpenDossier,
    openDossiers,
    uncancelledDossiers,
  } = useDossierActivationCheck({
    dossierId,
  });
  const selectedDossier = activatedDossiers.find(
    (dossier) => dossier.id === dossierId
  );
  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "dossier-activated-view",
    contextIds: {
      opportunityId: selectedDossier?.opportunity?.id,
      dossierId: dossierId,
    },
  });

  const config = getPublicConfig();
  const crmClientURL = config.isSuccess ? getCRMClientURL(config.data) : null;

  const { mutateAsync, isLoading: isQualifyingNewLead } = qualifyNewLead();
  const {
    mutateAsync: qualifyNewLeadForContactAsync,
    isLoading: isQualifyingNewLeadForContact,
  } = qualifyNewLeadForContact({
    mutationOptions: {
      successAlertMessage:
        "New opportunity created, it can be accessed at the bottom of this page",
    },
  });

  const openOpportunity = (opportunityId: string) => {
    if (crmClientURL && opportunityId) {
      openInNewTab(getOpportunityCRMUrl(crmClientURL, opportunityId));
    }
  };

  const onNewCar = async () => {
    if (inAppScope("internal") && selectedDossier?.crmContactId) {
      // If internal then we qualify a new lead for the contact
      await qualifyNewLeadForContactAsync({
        contactId: selectedDossier.crmContactId,
      });
    } else if (inAppScope("external")) {
      // If external then we navigate to the latest open dossier if it exists, otherwise we create a new quote
      if (lastestOpenDossier) {
        navigate(getScopedPageUrl("viewDossier", lastestOpenDossier.id));
      } else {
        const ok = await confirm({
          title: "Create new quote?",
          description: "Are you sure you want to create a new quote?",
        });

        if (ok) {
          await mutateAsync();
          navigate(`${getScopedPageUrl("viewDossiers")}?openFirst=true`, {
            replace: true,
          });
        }
      }
    }
  };

  const renderOpenOpportunity = (opportunity: Opportunity) => {
    return (
      <Box key={opportunity.id}>
        {opportunity && (
          <APButtonLink
            text={opportunity.name ?? ""}
            onClick={() => openOpportunity(opportunity.id)}
            size="large"
            noUnderline={true}
          />
        )}
      </Box>
    );
  };

  const isLoading = isLoadingDossierActivationCheck || isLoadingFaqs;
  const isSuccess = isSuccessDossierActivationCheck && isSuccessFaqs;

  return (
    <PageContainer
      title={
        selectedDossier &&
        `My ${DossierStatusLabelMap[selectedDossier.status]} Car`
      }
      loading={
        isLoading ||
        !navigationCheckComplete ||
        isQualifyingNewLead ||
        isQualifyingNewLeadForContact
      }
    >
      {isSuccess &&
        navigationCheckComplete &&
        dossierId &&
        !isQualifyingNewLead &&
        !isQualifyingNewLeadForContact && (
          <>
            {selectedDossier && (
              <Stack spacing={2}>
                <Box>
                  <DossierSelector
                    dossiers={activatedDossiers}
                    selectedDossier={selectedDossier}
                    redirectTo="dossierActivated"
                    showDivider
                  />

                  <Box mt={2}>
                    <DossierSavingsChart
                      dossierId={selectedDossier.id}
                      showTotals
                      faqs={{
                        ...faqProps,
                      }}
                      faqAppLocationIdentifiers={{
                        gstSavings: "ActivatedDossierHome-RunningCostGST",
                        incomeTaxSavings: "ActivatedDossierHome-IncomeTaxSaved",
                        totalSavings: "ActivatedDossierHome-TotalSavings",
                      }}
                    />
                  </Box>
                </Box>

                <Stack spacing={1}>
                  {["Closed", "Closing"].includes(selectedDossier.status) &&
                    selectedDossier.terminationId &&
                    selectedDossier.closureStatus === "aware" && (
                      <MenuCard
                        title={"Termination"}
                        primaryButton={
                          <APButton
                            text="View"
                            variant="contained"
                            onClick={() => {
                              navigate(
                                getScopedPageUrl(
                                  "dossierViewTermination",
                                  selectedDossier.id
                                )
                              );
                            }}
                          />
                        }
                      />
                    )}
                  <MenuCard
                    title="Balance"
                    titleLink={getScopedPageUrl(
                      "dossierBalances",
                      selectedDossier.id
                    )}
                    primaryButton={
                      <APButton
                        text="Transactions"
                        variant="contained"
                        onClick={() => {
                          navigate(
                            getScopedPageUrl(
                              "dossierTransactions",
                              selectedDossier.id
                            )
                          );
                        }}
                      />
                    }
                    secondaryButton={
                      <APButton
                        text="Budget"
                        variant="contained"
                        onClick={() => {
                          navigate(
                            getScopedPageUrl(
                              "dossierBudgets",
                              selectedDossier.id
                            )
                          );
                        }}
                      />
                    }
                  />
                  <MenuCard
                    title="Expense Claims"
                    titleLink={getScopedPageUrl(
                      "dossierExpenseClaims",
                      selectedDossier.id
                    )}
                    primaryButton={
                      isDossierActive(selectedDossier) && (
                        <RestrictedAppScope scope="external">
                          <APButton
                            text="New Claim"
                            variant="contained"
                            onClick={() => {
                              navigate(
                                getScopedPageUrl(
                                  "createDossierExpenseClaim",
                                  selectedDossier.id
                                )
                              );
                            }}
                            noWrap
                          />
                        </RestrictedAppScope>
                      )
                    }
                    secondaryButton={
                      <RestrictedAppScope scope="external">
                        <APButton
                          text="Update Bank"
                          variant="contained"
                          onClick={() => {
                            navigate(getScopedPageUrl("userDirectDebit"));
                          }}
                          noWrap
                        />
                      </RestrictedAppScope>
                    }
                  />
                  <MenuCard
                    title="Documents"
                    primaryButton={
                      <APButton
                        text="View"
                        variant="contained"
                        onClick={() => {
                          navigate(
                            getScopedPageUrl(
                              "dossierDocuments",
                              selectedDossier.id
                            )
                          );
                        }}
                      />
                    }
                  />

                  <Box>
                    <FaqList
                      pageId="dossier-activated-view"
                      appLocationIdentifier="ActivatedDossierHome-FAQList"
                      faqContextIds={{
                        opportunityId: selectedDossier.opportunity?.id,
                        dossierId: dossierId,
                      }}
                    />
                  </Box>
                </Stack>

                <Box
                  sx={{
                    pt: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Stack
                    spacing={1}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <RestrictedAppScope scope="external">
                      {uncancelledDossiers.length > 1 && (
                        <HomeButton destination="dossiersHome" />
                      )}
                    </RestrictedAppScope>
                    <RestrictedAppScope scope="external">
                      <APContactHelpButton
                        regardingOpportunityId={
                          selectedDossier.opportunity?.id ?? undefined
                        }
                      />
                    </RestrictedAppScope>
                  </Stack>
                  <Stack spacing={1} sx={{ alignItems: "flex-end" }}>
                    <Box>
                      <APButton
                        text="Initial Quote"
                        color="primary"
                        variant="contained"
                        onClick={() => {
                          navigate(
                            getScopedPageUrl("viewDossier", selectedDossier.id)
                          );
                        }}
                      />
                    </Box>

                    {inAppScope("external") ? (
                      <APButton
                        text="New Quote"
                        color="secondary"
                        variant="contained"
                        icon="car"
                        onClick={onNewCar}
                      />
                    ) : null}
                  </Stack>
                </Box>

                {inAppScope("internal") && (
                  <Box>
                    <APAccordion title="Open Opportunities" defaultExpanded>
                      <Stack spacing={2} mt={1}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Box>
                            <APButton
                              text="New Opportunity"
                              color="secondary"
                              variant="contained"
                              icon="car"
                              onClick={onNewCar}
                            />
                          </Box>
                        </Box>
                        <Stack spacing={1}>
                          {openDossiers.length === 0 && (
                            <EmptyList message="No open opportunities" />
                          )}
                          {openDossiers.length > 0 &&
                            openDossiers.map((dossier) =>
                              dossier.opportunity
                                ? renderOpenOpportunity(dossier.opportunity)
                                : null
                            )}
                        </Stack>
                      </Stack>
                    </APAccordion>
                  </Box>
                )}
              </Stack>
            )}
            {!selectedDossier && (
              <Typography variant="bodyMedium">
                Dossier is not activated
              </Typography>
            )}
          </>
        )}
    </PageContainer>
  );
};

export default DossierActivated;
